import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { Mail, Lock, User as UserIcon, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      if (mode === "login") { await login(form.email, form.password); toast.success("Welcome back!"); }
      else { await register(form.email, form.password, form.name); toast.success("Account created!"); }
      nav("/", { replace: true });
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const googleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="pt-16 min-h-screen" data-testid="login-page">
      <div className="max-w-md mx-auto px-6 py-12">
        <p className="text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase mb-2">Members Only</p>
        <h1 className="font-heading text-4xl font-bold mb-6">{mode === "login" ? "Welcome Back" : "Join VOTLiving"}</h1>

        <button onClick={googleAuth} className="w-full flex items-center justify-center gap-3 bg-white text-[#050507] font-semibold py-3 rounded-full mb-4 hover:bg-white/90 transition-colors" data-testid="google-signin-btn">
          <svg viewBox="0 0 48 48" className="w-5 h-5"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-4"><div className="flex-1 h-px bg-white/10" /><span className="text-xs text-white/40 tracking-widest">OR</span><div className="flex-1 h-px bg-white/10" /></div>

        <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
          {mode === "register" && (
            <Field icon={<UserIcon className="w-4 h-4" />} placeholder="Full name" value={form.name} onChange={v => setForm({...form, name: v})} testid="register-name" />
          )}
          <Field icon={<Mail className="w-4 h-4" />} placeholder="Email" type="email" value={form.email} onChange={v => setForm({...form, email: v})} testid="auth-email" />
          <Field icon={<Lock className="w-4 h-4" />} placeholder="Password" type="password" value={form.password} onChange={v => setForm({...form, password: v})} testid="auth-password" />
          {err && <p className="text-[#E11D48] text-sm" data-testid="auth-error">{err}</p>}
          <button type="submit" disabled={loading} className="w-full btn-gold py-3 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="auth-submit">
            {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            {!loading && <ChevronRight className="w-4 h-4" />}
          </button>
        </form>

        <p className="mt-6 text-sm text-white/60 text-center">
          {mode === "login" ? "New here? " : "Already have an account? "}
          <button className="text-[#D4AF37] hover:underline" onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }} data-testid="auth-toggle-mode">
            {mode === "login" ? "Create an account" : "Sign in instead"}
          </button>
        </p>

        {mode === "login" && (
          <p className="mt-3 text-center text-sm">
            <Link to="/forgot-password" className="text-white/60 hover:text-[#D4AF37]" data-testid="forgot-password-link">Forgot password?</Link>
          </p>
        )}

        <p className="mt-8 text-center text-xs text-white/40">
          <Link to="/" className="hover:text-white">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ icon, testid, ...props }) {
  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">{icon}</div>
      <input {...props} onChange={(e) => props.onChange(e.target.value)} required className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60 focus:bg-white/10" data-testid={testid} />
    </div>
  );
}
